import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Clock, FileSpreadsheet, Filter, RefreshCw, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { api, formatDate, getStatusColor } from '../services/api';

interface FaseRelatorio {
  id: number;
  ordem: number;
  nome_fase: string;
  status_fase: string;
  timestamp_inicio_rota: string | null;
  timestamp_chegada: string | null;
  motorista_nome: string | null;
  duracao_segundos: number | null;
}

interface OsRelatorio {
  id: number;
  codigo_acesso: string;
  nome_falecido: string;
  numero_nota_contratacao: string | number | null;
  status_geral: string;
  criado_em: string;
  inicio_primeira_fase: string | null;
  fim_ultima_fase: string | null;
  tempo_total_calendario_segundos: number | null;
  tempo_total_soma_fases_segundos: number | null;
  fases: FaseRelatorio[];
}

const formatDuration = (seconds: number | null | undefined): string => {
  if (seconds == null || Number.isNaN(Number(seconds))) return '—';
  const total = Math.max(0, Math.floor(Number(seconds)));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}min`;
  if (m > 0) return `${m}min ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
};

type TipoPeriodoRegulador = 'mensal' | 'trimestral' | 'anual' | 'personalizado';
type PlanilhaRegulador = 'AA1' | 'AA2';

const hoje = new Date();
const ANO_ATUAL = hoje.getFullYear();
const ANOS_DISPONIVEIS = Array.from({ length: 6 }, (_, i) => ANO_ATUAL - i);

const toISODate = (ano: number, mes: number, dia: number): string =>
  `${ano}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;

const ultimoDiaDoMes = (ano: number, mes: number): number => new Date(ano, mes, 0).getDate();

const formatarDataBR = (iso: string): string => iso.split('-').reverse().join('-');

const extrairErroBlob = async (error: any): Promise<string | undefined> => {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      return JSON.parse(await data.text())?.error;
    } catch {
      return undefined;
    }
  }
  return data?.error;
};

const RelatorioTempos: React.FC = () => {
  const [items, setItems] = useState<OsRelatorio[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [filters, setFilters] = useState({
    data_inicio: '',
    data_fim: '',
    status: '',
    busca: ''
  });
  const [buscaInput, setBuscaInput] = useState('');
  const [exportando, setExportando] = useState<PlanilhaRegulador | null>(null);
  const [periodoRegulador, setPeriodoRegulador] = useState({
    tipo: 'mensal' as TipoPeriodoRegulador,
    ano: ANO_ATUAL,
    mes: hoje.getMonth() + 1,
    trimestre: Math.floor(hoje.getMonth() / 3) + 1,
    data_inicio: '',
    data_fim: ''
  });

  const calcularPeriodoRegulador = (): { data_inicio: string; data_fim: string } | null => {
    const { tipo, ano, mes, trimestre, data_inicio, data_fim } = periodoRegulador;
    if (tipo === 'mensal') {
      return { data_inicio: toISODate(ano, mes, 1), data_fim: toISODate(ano, mes, ultimoDiaDoMes(ano, mes)) };
    }
    if (tipo === 'trimestral') {
      const mesInicial = (trimestre - 1) * 3 + 1;
      const mesFinal = mesInicial + 2;
      return {
        data_inicio: toISODate(ano, mesInicial, 1),
        data_fim: toISODate(ano, mesFinal, ultimoDiaDoMes(ano, mesFinal))
      };
    }
    if (tipo === 'anual') {
      return { data_inicio: toISODate(ano, 1, 1), data_fim: toISODate(ano, 12, 31) };
    }
    if (!data_inicio || !data_fim) return null;
    return { data_inicio, data_fim };
  };

  const exportarPlanilha = async (planilha: PlanilhaRegulador) => {
    const periodo = calcularPeriodoRegulador();
    if (!periodo) {
      toast.error('Informe as datas inicial e final do período.');
      return;
    }
    if (periodo.data_fim < periodo.data_inicio) {
      toast.error('A data final deve ser igual ou posterior à inicial.');
      return;
    }

    try {
      setExportando(planilha);
      const response = await api.get(`/os/relatorio-tempos/${planilha.toLowerCase()}`, {
        params: periodo,
        responseType: 'blob',
        timeout: 180000
      });

      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Planilha ${planilha} - ${formatarDataBR(periodo.data_inicio)} a ${formatarDataBR(periodo.data_fim)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success(`Planilha ${planilha} gerada com sucesso`);
    } catch (error: any) {
      console.error(`Erro ao exportar Planilha ${planilha}:`, error);
      toast.error((await extrairErroBlob(error)) || `Erro ao gerar a Planilha ${planilha}`);
    } finally {
      setExportando(null);
    }
  };

  const loadReport = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.data_inicio) params.append('data_inicio', filters.data_inicio);
      if (filters.data_fim) params.append('data_fim', filters.data_fim);
      if (filters.status) params.append('status', filters.status);
      if (filters.busca.trim()) params.append('busca', filters.busca.trim());

      const qs = params.toString();
      const response = await api.get(`/os/relatorio-tempos${qs ? `?${qs}` : ''}`);
      setItems(response.data?.items || []);
    } catch (error: any) {
      console.error('Erro ao carregar relatório:', error);
      toast.error(error.response?.data?.error || 'Erro ao carregar relatório de tempos');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const toggleExpand = (id: number) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const applyBusca = () => {
    setFilters((prev) => ({ ...prev, busca: buscaInput }));
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <Clock className="h-7 w-7 text-blue-600" />
            Relatório de Tempos
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            OSs iniciadas com horário de início/fim por fase, duração de cada fase e tempo total.
          </p>
        </div>
        <button
          type="button"
          onClick={loadReport}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
          <FileSpreadsheet className="h-4 w-4 text-green-600" />
          Planilhas do órgão regulador (AA1 e AA2)
        </div>
        <div className="flex flex-col lg:flex-row lg:items-end gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Período</label>
            <select
              value={periodoRegulador.tipo}
              onChange={(e) => setPeriodoRegulador((prev) => ({ ...prev, tipo: e.target.value as TipoPeriodoRegulador }))}
              className="w-full lg:w-44 rounded-md border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="mensal">Mensal</option>
              <option value="trimestral">Trimestral</option>
              <option value="anual">Anual</option>
              <option value="personalizado">Personalizado</option>
            </select>
          </div>

          {periodoRegulador.tipo === 'mensal' && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">Mês</label>
              <select
                value={periodoRegulador.mes}
                onChange={(e) => setPeriodoRegulador((prev) => ({ ...prev, mes: Number(e.target.value) }))}
                className="w-full lg:w-40 rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {new Date(2000, i, 1).toLocaleDateString('pt-BR', { month: 'long' })}
                  </option>
                ))}
              </select>
            </div>
          )}

          {periodoRegulador.tipo === 'trimestral' && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">Trimestre</label>
              <select
                value={periodoRegulador.trimestre}
                onChange={(e) => setPeriodoRegulador((prev) => ({ ...prev, trimestre: Number(e.target.value) }))}
                className="w-full lg:w-44 rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                <option value={1}>1º (jan–mar)</option>
                <option value={2}>2º (abr–jun)</option>
                <option value={3}>3º (jul–set)</option>
                <option value={4}>4º (out–dez)</option>
              </select>
            </div>
          )}

          {periodoRegulador.tipo !== 'personalizado' && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">Ano</label>
              <select
                value={periodoRegulador.ano}
                onChange={(e) => setPeriodoRegulador((prev) => ({ ...prev, ano: Number(e.target.value) }))}
                className="w-full lg:w-28 rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                {ANOS_DISPONIVEIS.map((ano) => (
                  <option key={ano} value={ano}>{ano}</option>
                ))}
              </select>
            </div>
          )}

          {periodoRegulador.tipo === 'personalizado' && (
            <>
              <div>
                <label className="block text-xs text-gray-500 mb-1">De</label>
                <input
                  type="date"
                  value={periodoRegulador.data_inicio}
                  onChange={(e) => setPeriodoRegulador((prev) => ({ ...prev, data_inicio: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Até</label>
                <input
                  type="date"
                  value={periodoRegulador.data_fim}
                  onChange={(e) => setPeriodoRegulador((prev) => ({ ...prev, data_fim: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
            </>
          )}

          {(['AA1', 'AA2'] as PlanilhaRegulador[]).map((planilha) => (
            <button
              key={planilha}
              type="button"
              onClick={() => exportarPlanilha(planilha)}
              disabled={exportando !== null}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <FileSpreadsheet className={`h-4 w-4 ${exportando === planilha ? 'animate-pulse' : ''}`} />
              {exportando === planilha ? 'Gerando…' : `Exportar ${planilha}`}
            </button>
          ))}
        </div>
        <p className="mt-3 text-xs text-gray-400">
          Inclui apenas OS do Nexus vindas do Sisfuner, filtradas pela data da contratação. Os dados de contratação,
          tarifa, nota fiscal e observações são consultados no Sisfuner no momento da exportação (máximo de 366 dias).
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
          <Filter className="h-4 w-4" />
          Filtros
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Início (de)</label>
            <input
              type="date"
              value={filters.data_inicio}
              onChange={(e) => setFilters((prev) => ({ ...prev, data_inicio: e.target.value }))}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Início (até)</label>
            <input
              type="date"
              value={filters.data_fim}
              onChange={(e) => setFilters((prev) => ({ ...prev, data_fim: e.target.value }))}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Status da OS</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="">Todos</option>
              <option value="Em Andamento">Em Andamento</option>
              <option value="Concluída">Concluída</option>
              <option value="Pendente">Pendente</option>
            </select>
          </div>
          <div className="lg:col-span-2">
            <label className="block text-xs text-gray-500 mb-1">Busca</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={buscaInput}
                onChange={(e) => setBuscaInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applyBusca()}
                placeholder="Falecido, nota ou código"
                className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
              <button
                type="button"
                onClick={applyBusca}
                className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
              >
                <Search className="h-4 w-4" />
                Buscar
              </button>
            </div>
          </div>
        </div>
        <p className="mt-3 text-xs text-gray-400">
          Tempo total (calendário) = do 1º início ao último fechamento. Soma das fases = soma das durações individuais (sem intervalos entre fases).
        </p>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <span className="text-sm text-gray-600">
            {loading ? 'Carregando…' : `${items.length} OS iniciada(s)`}
          </span>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-500">Carregando relatório…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            Nenhuma OS iniciada encontrada com os filtros atuais.
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {items.map((os) => {
              const isOpen = !!expanded[os.id];
              return (
                <div key={os.id}>
                  <button
                    type="button"
                    onClick={() => toggleExpand(os.id)}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-start gap-3"
                  >
                    <span className="mt-1 text-gray-400">
                      {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </span>
                    <div className="flex-1 min-w-0 grid grid-cols-1 lg:grid-cols-6 gap-2 lg:gap-4">
                      <div className="lg:col-span-2">
                        <div className="font-medium text-gray-900 truncate">
                          {os.nome_falecido || '—'}
                        </div>
                        <div className="text-xs text-gray-500">
                          Nota {os.numero_nota_contratacao || '—'} · {os.codigo_acesso}
                        </div>
                      </div>
                      <div>
                        <span className={`inline-flex px-2 py-0.5 text-xs rounded border ${getStatusColor(os.status_geral)}`}>
                          {os.status_geral}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">
                        <div className="text-xs text-gray-400">Início</div>
                        {formatDate(os.inicio_primeira_fase || '')}
                      </div>
                      <div className="text-sm text-gray-600">
                        <div className="text-xs text-gray-400">Fim</div>
                        {formatDate(os.fim_ultima_fase || '')}
                      </div>
                      <div className="text-sm text-gray-900">
                        <div className="text-xs text-gray-400">Total (calendário)</div>
                        <span className="font-medium">{formatDuration(os.tempo_total_calendario_segundos)}</span>
                        <div className="text-xs text-gray-500">
                          Soma fases: {formatDuration(os.tempo_total_soma_fases_segundos)}
                        </div>
                      </div>
                    </div>
                  </button>

                  {isOpen && (
                    <div className="px-4 pb-4 bg-gray-50">
                      <div className="overflow-x-auto border border-gray-200 rounded-md bg-white">
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-100 text-gray-600 text-xs uppercase">
                            <tr>
                              <th className="px-3 py-2 text-left">#</th>
                              <th className="px-3 py-2 text-left">Fase</th>
                              <th className="px-3 py-2 text-left">Status</th>
                              <th className="px-3 py-2 text-left">Motorista</th>
                              <th className="px-3 py-2 text-left">Início</th>
                              <th className="px-3 py-2 text-left">Fim</th>
                              <th className="px-3 py-2 text-left">Duração</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {os.fases.map((fase) => (
                              <tr key={fase.id}>
                                <td className="px-3 py-2 text-gray-500">{fase.ordem}</td>
                                <td className="px-3 py-2 text-gray-900">{fase.nome_fase}</td>
                                <td className="px-3 py-2">
                                  <span className={`inline-flex px-2 py-0.5 text-xs rounded border ${getStatusColor(fase.status_fase)}`}>
                                    {fase.status_fase}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-gray-600">{fase.motorista_nome || '—'}</td>
                                <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                                  {formatDate(fase.timestamp_inicio_rota || '')}
                                </td>
                                <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                                  {formatDate(fase.timestamp_chegada || '')}
                                </td>
                                <td className="px-3 py-2 font-medium text-gray-900">
                                  {formatDuration(fase.duracao_segundos)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default RelatorioTempos;
